// KinBridge Health — web API client. Mirrors mobile/src/api.ts against the
// shared FastAPI backend. Web auth uses an httpOnly cookie set by the backend
// (token never touches JS/localStorage). A readable double-submit CSRF cookie
// is echoed back in the X-CSRF-Token header on mutating requests.
// Backend base URL. In the deployed setup nginx serves BOTH the frontend and the
// backend on the same host, so we stay SAME-ORIGIN ("") for every deployed
// hostname — this keeps httpOnly cookies working without cross-site CORS. An
// explicit REACT_APP_CAREGIVER_API_BASE still wins if set.
function resolveApiBase() {
  if (process.env.REACT_APP_CAREGIVER_API_BASE) return process.env.REACT_APP_CAREGIVER_API_BASE;
  // Same-origin for all known deployed/preview hosts (frontend + backend behind
  // one nginx): scribe-test.preview.emergentagent.com, scribe-test.emergent.host,
  // caregiver.kinbridgehealth.com. Falling through also returns "" (same-origin).
  return "";
}
const BASE = resolveApiBase();

function getCookie(name) {
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : "";
}

// Single-flight silent refresh: on a 401 we rotate the refresh cookie once and
// retry the original request, so 8h access expiry renews transparently.
let refreshPromise = null;
function tryRefresh() {
  if (!refreshPromise) {
    refreshPromise = fetch(`${BASE}/api/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { "X-CSRF-Token": getCookie("kb_csrf") },
    })
      .then((r) => r.ok)
      .catch(() => false)
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

const NO_REFRESH = ["/auth/login", "/auth/signup", "/auth/refresh", "/auth/logout"];

async function request(path, opts = {}, _retried = false) {
  const method = (opts.method || "GET").toUpperCase();
  const headers = { "Content-Type": "application/json", ...(opts.headers || {}) };
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    const csrf = getCookie("kb_csrf");
    if (csrf) headers["X-CSRF-Token"] = csrf;
  }
  const res = await fetch(`${BASE}/api${path}`, { ...opts, headers, credentials: "include" });
  if (res.status === 401 && !_retried && !NO_REFRESH.some((p) => path.startsWith(p))) {
    const ok = await tryRefresh();
    if (ok) return request(path, opts, true);
  }
  const text = await res.text();
  // CloudFront turns a 403 or 404 from ANY origin into `/index.html` with a
  // 200, including responses proxied through /api/*. Custom error responses
  // are a distribution-level setting and cannot be scoped to exclude the API
  // behaviour, and an attempt to replace them with a Lambda@Edge trigger on
  // 2026-09-17 broke every client route (see infra/terraform/web.tf). So a
  // refused or missing API resource arrives here as a successful HTML page.
  //
  // Found 2026-10-09: an already-used family invite token answered 404 at the
  // API and 200 text/html at caregiver.kinbridgehealth.com, so the invite page
  // treated the index document as an empty invite and rendered a blank signup
  // form instead of "this invitation is no longer valid". Every API 404 and
  // 403 in this app had the same shape.
  if (/^\s*<(?:!doctype|html)/i.test(text)) {
    // User-facing wording, not "Request failed (404)": callers surface
    // `message` when a response carries no `detail`, and this is the page an
    // invited person lands on after clicking a link that has already been used.
    const err = new Error("That link is no longer valid, or has already been used.");
    // Deliberately no `detail`: the edge has discarded the status, so we
    // cannot tell a 403 from a 404 and must not invent one. Callers fall back
    // to their own wording.
    err.status = 404;
    err.htmlFallback = true;
    throw err;
  }
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = { detail: text }; }
  if (!res.ok) {
    const err = new Error(data?.detail || `Request failed (${res.status})`);
    err.status = res.status;
    err.detail = data?.detail;
    throw err;
  }
  return data;
}

export { request };

export const api = {
  // auth
  signup: (email, password, name, attribution) =>
    request("/auth/signup", {
      method: "POST",
      body: JSON.stringify({
        email, password, name,
        ...(attribution && Object.keys(attribution).length ? { attribution } : {}),
      }),
    }, false),
  login: (email, password) =>
    request("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }, false),
  googleStart: (redirect_uri) =>
    request(`/auth/google/start?redirect_uri=${encodeURIComponent(redirect_uri)}`, {}, false),
  googleCallback: (code, state, redirect_uri) =>
    request("/auth/google/callback", { method: "POST", body: JSON.stringify({ code, state, redirect_uri }) }, false),
  logout: () => request("/auth/logout", { method: "POST" }),
  me: () => request("/auth/me"),
  // family
  createFamily: (b) => request("/families", { method: "POST", body: JSON.stringify(b) }),
  setLovedOne: (b) => request("/families/loved-one", { method: "PUT", body: JSON.stringify(b) }),
  myFamily: () => request("/families/mine"),
  invite: (email, role) => request("/families/invite", { method: "POST", body: JSON.stringify({ email, role }) }),
  // meds
  listMeds: () => request("/meds"),
  createMed: (b) => request("/meds", { method: "POST", body: JSON.stringify(b) }),
  logMed: (b) => request("/meds/log", { method: "POST", body: JSON.stringify(b) }),
  getMedLogs: (date) => request(`/meds/logs${date ? `?date=${date}` : ""}`),
  scanMed: (b) => request("/meds/scan", { method: "POST", body: JSON.stringify(b) }),
  // events
  listEvents: () => request("/events"),
  createEvent: (b) => request("/events", { method: "POST", body: JSON.stringify(b) }),
  signoffEvent: (id, summary) => request(`/events/${id}/signoff`, { method: "POST", body: JSON.stringify({ summary }) }),
  deleteEvent: (id, scope = "this") => request(`/events/${id}?scope=${scope}`, { method: "DELETE" }),
  // vitals
  listVitals: (kind, patient_id) => {
    const qs = new URLSearchParams();
    if (kind) qs.set("kind", kind);
    if (patient_id) qs.set("patient_id", patient_id);
    const q = qs.toString();
    return request(`/vitals${q ? `?${q}` : ""}`);
  },
  createVital: (b) => request("/vitals", { method: "POST", body: JSON.stringify(b) }),
  importVitals: (b) => request("/vitals/import", { method: "POST", body: JSON.stringify(b) }),
  // activity
  activity: () => request("/activity?limit=100"),
  // visit tasks
  visitTasks: (status) => request(`/visit-tasks${status ? `?status_filter=${status}` : ""}`),
  updateVisitTask: (id, status) => request(`/visit-tasks/${id}`, { method: "PATCH", body: JSON.stringify({ status }) }),
  // vault
  blobStatus: () => request("/blobs/status"),
  blobList: () => request("/blobs/list"),
  blobDownloadUrl: (s3_key) => request("/blobs/download-url", { method: "POST", body: JSON.stringify({ s3_key }) }),
  // billing
  billingPlans: () => request("/billing/plans", {}, false),
  billingStatus: () => request("/billing/status"),
  billingCheckout: (plan_id, promo_code) => request("/billing/checkout", { method: "POST", body: JSON.stringify(promo_code ? { plan_id, promo_code } : { plan_id }) }),
  validatePromo: (code) => request("/billing/promo/validate", { method: "POST", body: JSON.stringify({ code }) }, false),
  promoPreview: (code, plan_id) => request(`/billing/promo/preview?plan_id=${encodeURIComponent(plan_id || "basic")}`, { method: "POST", body: JSON.stringify({ code }) }, false),
  billingCancel: () => request("/billing/cancel", { method: "POST" }),
  // feedback (pilot win prompt + testimonial consent)
  feedbackPending: () => request("/feedback/pending"),
  feedbackAnswer: (id, answer) => request(`/feedback/${id}/answer`, { method: "POST", body: JSON.stringify({ answer }) }),
  feedbackConsent: (id, choice) => request(`/feedback/${id}/consent`, { method: "POST", body: JSON.stringify({ choice }) }),
  feedbackDismiss: (id) => request(`/feedback/${id}/dismiss`, { method: "POST" }),
  feedbackMine: () => request("/feedback/mine"),
  feedbackWithdraw: (id) => request(`/feedback/${id}/withdraw`, { method: "POST" }),
  // visits
  listVisits: () => request("/visits"),
  createVisit: (b) => request("/visits", { method: "POST", body: JSON.stringify(b) }),
  createVisitAudio: (b) => request("/visits/audio", { method: "POST", body: JSON.stringify(b) }),
  // eobs
  listEobs: () => request("/eobs"),
  createEob: (b) => request("/eobs", { method: "POST", body: JSON.stringify(b) }),
  uploadEob: (b) => request("/eobs/upload", { method: "POST", body: JSON.stringify(b) }),
  draftAppeal: (id, b) => request(`/eobs/${id}/appeal`, { method: "POST", body: JSON.stringify(b) }),
  updateEobStatus: (id, status) => request(`/eobs/${id}/status`, { method: "PATCH", body: JSON.stringify({ status }) }),
  emailAppeal: (id) => request(`/eobs/${id}/email`, { method: "POST" }),
  // messages
  messagesInbox: (unreadOnly = false) => request(`/messages/inbox${unreadOnly ? "?unread_only=true" : ""}`),
  messagesUnreadCount: () => request("/messages/unread-count"),
  messagesMarkRead: (id) => request(`/messages/${id}/read`, { method: "PATCH" }),
  messagesMarkAllRead: () => request("/messages/read-all", { method: "PATCH" }),
  // sessions
  listSessions: () => request("/auth/sessions"),
  revokeSession: (id) => request(`/auth/sessions/${id}/revoke`, { method: "POST" }),
  revokeOtherSessions: () => request("/auth/sessions/revoke-others", { method: "POST" }),
  // consent
  consentStatus: () => request("/consent/ambient"),
  consentGrant: (b) => request("/consent/ambient", { method: "POST", body: JSON.stringify(b) }),
  consentRevoke: (reason) => request("/consent/ambient/revoke", { method: "POST", body: JSON.stringify({ reason: reason ?? null }) }),
  consentDisclosure: () => request("/consent/ambient/disclosure"),
  // patients
  listPatients: () => request("/patients"),
  createPatient: (b) => request("/patients", { method: "POST", body: JSON.stringify(b) }),
  updatePatient: (id, b) => request(`/patients/${id}`, { method: "PATCH", body: JSON.stringify(b) }),
  deletePatient: (id) => request(`/patients/${id}`, { method: "DELETE" }),
  // public token flows (no auth required)
  peekPatientInvite: (token) => request(`/auth/patient/invite/${token}`, {}, false),
  acceptPatientInvite: (b) => request("/auth/patient/accept", { method: "POST", body: JSON.stringify(b) }, false),
  // Family-member invites. peek/accept are public (the emailed token is the
  // proof of address ownership); acceptFamilyInviteAsUser is for someone who
  // already has an account and is signed in.
  peekFamilyInvite: (token) => request(`/auth/family/invite/${token}`, {}, false),
  acceptFamilyInvite: (b) => request("/auth/family/accept", { method: "POST", body: JSON.stringify(b) }, false),
  acceptFamilyInviteAsUser: (token) => request("/families/invite/accept", { method: "POST", body: JSON.stringify({ token }) }),
  resendInvite: (email) => request("/families/invite/resend", { method: "POST", body: JSON.stringify({ email }) }),
  revokeInvite: (email) => request("/families/invite/revoke", { method: "POST", body: JSON.stringify({ email }) }),
  removeMember: (userId, transferTo) =>
    request(`/families/members/${userId}/remove`, {
      method: "POST",
      body: JSON.stringify(transferTo ? { transfer_to: transferTo } : {}),
    }),
  patientMe: () => request("/patient/me"),
  activationLookup: (token) => request(`/employer/activate/${token}`, {}, false),
  activateSeat: (token, b) => request(`/employer/activate/${token}`, { method: "POST", body: JSON.stringify(b) }, false),
};
